import { mapGetters } from 'vuex';

export default {
  name: 'MitraAttendance',
  data() {
    return {
      selectedCourse: null,
      selectedBatch: null,
      selectedBatchIndex: 0,
      selectedAttendanceTrainingEventId: null,
      selectedAttendanceScheduleId: null,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 50, 100],
      filters: {
        search: '',
        status: null
      },
      statusOptions: [
        { value: 'Open', label: 'Open' },
        { value: 'Closed', label: 'Closed' }
      ],
      selectedPeriod: null,
      searchTimeout: null,
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV
    };
  },
  computed: {
    ...mapGetters('mitraAttendance', ['courses', 'pagination', 'loading', 'error', 'summary', 'summaryLoading']),
    ...mapGetters('tnaPeriod', { periods: 'periods', periodsLoading: 'loading' }),
    
    // Period dropdown options from store
    periodOptions() {
      if (!this.periods || this.periods.length === 0) return [];
      
      return this.periods.map(p => ({
        label: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id,
        startDate: p.start_date,
        endDate: p.end_date,
        status: p.vstatus
      }));
    },
    
    isLoading() {
      return this.loading;
    },
    
    filteredCourses() {
      let filtered = this.courses || [];
      
      if (this.filters.search) {
        const searchLower = this.filters.search.toLowerCase();
        filtered = filtered.filter(course => 
          course.title.toLowerCase().includes(searchLower) ||
          course.description?.toLowerCase().includes(searchLower) ||
          course.provider_name?.toLowerCase().includes(searchLower)
        );
      }
      
      if (this.filters.status) {
        filtered = filtered.filter(course => 
          course.tna_status === this.filters.status
        );
      }
      
      return filtered;
    },
    
    paginatedCourses() {
      return this.courses || [];
    },
    
    totalItems() {
      return this.pagination?.total || 0;
    },
    
    totalPages() {
      return this.pagination?.totalPage || 0;
    },

    paginationStart() {
      if (!this.totalItems) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalItems);
    },
    
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages;
    }
  },
  
  watch: {
    'filters.search'() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchCourses();
      }, 500);
    },
    'filters.status'() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    selectedPeriod() {
      this.currentPage = 1;
      this.fetchSummary();
      this.fetchCourses();
    },
    currentPage() {
      this.fetchCourses();
    }
  },
  
  methods: {
    async fetchCourses() {
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage
        };
        
        if (this.filters.search) {
          params.search = this.filters.search;
        }
        
        if (this.filters.status) {
          params.tna_status = this.filters.status;
        }
        
        if (this.selectedPeriod) {
          params.tna_period = this.selectedPeriod;
        }
        
        await this.$store.dispatch('mitraAttendance/getCourses', params);
      } catch (error) {
        console.error('Error fetching courses:', error);
        this.$swal({
          title: 'Error',
          text: error.code === 'ECONNABORTED'
            ? 'Daftar pelatihan terlalu lama dimuat. Silakan coba lagi atau pilih periode yang lebih spesifik.'
            : error.message || 'Daftar pelatihan belum berhasil dimuat. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    async fetchSummary() {
      if (!this.selectedPeriod) return;
      
      try {
        await this.$store.dispatch('mitraAttendance/getSummary', {
          tna_period: this.selectedPeriod
        });
      } catch (error) {
        console.error('Error fetching summary:', error);
      }
    },
    
    async viewCourseDetail(course) {
      this.selectedCourse = course;
      // Show batch list modal - fetching will be handled by modal's @shown event
      this.$bvModal.show('modal-batch-list');
    },
    
    handleViewSchedule({ batch, index }) {
      this.selectedBatch = batch;
      this.selectedBatchIndex = index;
      
      // Show schedule modal
      this.$bvModal.show('modal-schedule');
    },

    handleViewAttendanceReport(trainingEventId) {
      this.selectedAttendanceTrainingEventId = trainingEventId;
      this.selectedAttendanceScheduleId = null;
      this.$bvModal.show('modal-attendance-schedules');
    },

    handleViewScheduleAttendance({ scheduleId, trainingEventId }) {
      this.selectedAttendanceTrainingEventId = trainingEventId;
      this.selectedAttendanceScheduleId = scheduleId;
      this.$bvModal.show('modal-attendance-schedules');
    },

    closeAttendanceReportModal() {
      this.selectedAttendanceTrainingEventId = null;
      this.selectedAttendanceScheduleId = null;
    },
    
    changePage(page) {
      if (page === '...' || page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    
    clearFilters() {
      this.filters = {
        search: '',
        status: null
      };
      this.currentPage = 1;
    },
    
    handleImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course';
    },

    handleImageProviderError(event) {
      event.target.src = 'https://dummyimage.com/16x16/ffffff/000000&text=Provider+title';
    },
    
    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || '';
      
      if (type.includes('certification') && !type.includes('semi')) {
        return 'fe fe-file-text';
      } else if (type.includes('semi')) {
        return 'fe fe-shield';
      } else if (type.includes('upskilling')) {
        return 'fe fe-trending-up';
      } else if (type.includes('reskilling')) {
        return 'fe fe-refresh-cw';
      } else if (type.includes('mandatory')) {
        return 'fe fe-alert-circle';
      } else {
        return 'fe fe-book-open';
      }
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        'SCHEDULE': 'badge bg-success',
        'NO SCHEDULE': 'badge bg-warning',
        'COMPLETED': 'badge bg-info',
        'CANCELLED': 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    },
    
    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return '';
      
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
      const year = end.getFullYear();
      
      return `${startMonth} - ${endMonth} ${year}`;
    },

    getDefaultPeriodOption() {
      if (!this.periodOptions.length) return null;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const openPeriod = this.periodOptions.find(period => {
        const start = new Date(period.startDate);
        const end = new Date(period.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        return today >= start && today <= end;
      });

      return openPeriod || this.periodOptions.find(period => period.status === 'open') || this.periodOptions[0];
    }
  },
  
  async mounted() {
    console.log('✅ Mitra Attendance page mounted');
    
    // Fetch TNA periods first
    try {
      await this.$store.dispatch('tnaPeriod/getTNAPeriods');
      
      if (this.periodOptions.length > 0) {
        this.selectedPeriod = this.getDefaultPeriodOption().value;
      } else {
        await this.fetchCourses();
      }
    } catch (error) {
      console.error('Error fetching periods:', error);
      await this.fetchCourses();
    }
  }
};
